'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
export const PageTransitionStatusContext = createContext<'load' | 'ready' | 'auto'>('auto');
export const usePageTransition = (onTransitionStatus: (status: 'load' | 'ready') => void) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const contextStatus = useContext(PageTransitionStatusContext);

  // useRef instead of useState to avoid unnecessary re-renders
  const lastStatus = useRef<'load' | 'ready' | undefined>(undefined);
  const onLoad = useCallback(() => {
    if (lastStatus.current === 'load') {
      return;
    }
    lastStatus.current = 'load';
    onTransitionStatus('load');
  }, [onTransitionStatus]);
  const onReady = useCallback(() => {
    if (lastStatus.current === 'ready') {
      return;
    }
    lastStatus.current = 'ready';
    onTransitionStatus('ready');
  }, [onTransitionStatus]);
  useEffect(() => {
    if (contextStatus === 'auto') {
      return;
    }
    onTransitionStatus(contextStatus);
  }, [contextStatus, onTransitionStatus]);

  /**
   * Detect navigation events by listening to all link clicks on the document.
   * This is a workaround because Next.js does not provide any way to hook into the
   * router events. With Next.js 15, the options are even more limited than before.
   */
  useEffect(() => {
    if (contextStatus !== 'auto') {
      return;
    }
    const handler = (event: Event) => {
      if (event.target instanceof HTMLElement) {
        const anchor = event.target.closest<HTMLAnchorElement>('a[href]');
        if (!anchor || anchor.target !== '_self' && anchor.target !== '') return;

        // Ignore links with data-no-page-transition attribute
        if (anchor.hasAttribute('data-no-page-transition')) return;

        // Ignore hash-only links (e.g., href="#" or href="#something")
        const href = anchor.getAttribute('href');
        if (href && href.startsWith('#')) return;
        const path = extractSameOriginPath(anchor.href);

        // Ignore external links and links with mxp parameter
        if (!path || !path.startsWith('/') || path.includes('mxp=')) return;
        onLoad();
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [contextStatus, onLoad]);

  /**
   * Listen for route ready.
   * This is the recommended way for Next.js (see doc link above).
   */
  useEffect(() => {
    if (contextStatus !== 'auto') {
      return;
    }
    setTimeout(() => onReady(), 100);
  }, [pathname, searchParams, onReady, contextStatus]);
  return null;
};
const extractSameOriginPath = (href: string): string | undefined => {
  try {
    const url = new URL(href);
    if (url.origin !== window.location.origin) return undefined;
    return url.pathname + url.search;
  } catch {
    return href;
  }
};